import React, {useMemo, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import clsx from 'clsx';

import {FEATURE_NAMES} from '../../../growthbook/constants';
import {BookmarkAddButton} from '../../../modules/bookmarks/bookmark-add-button';
import {SHARING_COPY, SharingEventLogger} from '../../../services/analytics/sharing';
import {ArticleReactionsWidget} from '../../components/article-reactions-widget.js';
import {CommentsIcon} from '../../components/comments-icon';
import {ReactionIcon} from '../../components/reaction-icon';
import {ShareIcon} from '../../components/share-icon';
import {SharingWidget} from '../../components/sharing-widget';
import {COMMENTS_MODAL_SEARCH_PARAM_VALUE, MODAL_SEARCH_PARAM_KEY} from '../../constants/url';
import {isFromGoogleNews} from '../../helpers/google-news';
import {useArticleData} from '../../hooks/use-article-data';
import useOnClickOutside from '../../hooks/use-click-outside';
import {useGrowthbook} from '../../hooks/use-growthbook';

import {VARIANTS_BUTTONS} from './constants';

import styles from './article-bottom-nav.module.css';

const getContainerStyles = variant => {
  switch (variant) {
    case VARIANTS_BUTTONS.COLORED_DIFFERENT_SIZES:
      return styles.coloredDifferentSizesContainer;
    case VARIANTS_BUTTONS.COLORED_SAME_SIZE:
      return styles.coloredSameSizeContainer;
    case VARIANTS_BUTTONS.DEFAULT_DESIGN:
    default:
      return styles.container;
  }
};

const ArticleBottomNav = ({articleId}) => {
  const growthBook = useGrowthbook();

  const variantButtons = useMemo(() => growthBook?.getFeatureValue(FEATURE_NAMES.ARTICLE_MENU_ABC) || '', [growthBook]);

  const {t} = useTranslation();
  const [isSharingModalOpen, setIsSharingModalOpen] = useState(false);
  const ref = useRef();
  const shareContainerRef = useRef();
  const reactionsButtonRef = useRef();
  const shareButtonRef = useRef();
  const commentsButtonRef = useRef();
  const [isReactionsOpen, setIsReactionsOpen] = useState(false);
  const data = useArticleData(articleId);
  const isMobile = window.Site.targeting.platform === 'smartphone';
  const layoutSection = isMobile ? 'bottom_navigation_bar' : 'left_navigation_bar';
  const variant = isMobile ? 'primary' : 'secondary';

  const shouldRenderShareButton = !isMobile || !isFromGoogleNews || Boolean(navigator.share);

  const onReactionsClick = () => {
    if (isSharingModalOpen) {
      setIsSharingModalOpen(false);
    }
    setIsReactionsOpen(!isReactionsOpen);
  };

  useOnClickOutside(ref, () => {
    setIsReactionsOpen(false);
  }, [reactionsButtonRef, shareButtonRef, commentsButtonRef]);

  useOnClickOutside(shareContainerRef, () => {
    setIsSharingModalOpen(false);
    SharingEventLogger.closeSharingByOverLay();
  }, [shareButtonRef, reactionsButtonRef, commentsButtonRef]);

  const onCommentsClick = () => {
    if (isSharingModalOpen) {
      setIsSharingModalOpen(false);
    }

    if (isReactionsOpen) {
      setIsReactionsOpen(false);
    }

    const url = new URL(data.url);

    url.searchParams.set(MODAL_SEARCH_PARAM_KEY, COMMENTS_MODAL_SEARCH_PARAM_VALUE);
    window.history.pushState({id: articleId}, null, url);
    const navEvent = new PopStateEvent('popstate');

    window.dispatchEvent(navEvent);
  };

  const onShareBlockClick = () => {
    if (isReactionsOpen) {
      setIsReactionsOpen(false);
    }

    SharingEventLogger.clickSharingIcon(layoutSection);
    if (!isMobile) {
      setIsSharingModalOpen(!isSharingModalOpen);
      return;
    }

    if (navigator && navigator.share) {
      const trackingUrl = new URL(data.url);

      trackingUrl.searchParams.append('utm_source', 'web-share-api');

      navigator.share({title: data.headline, url: trackingUrl.toString()}).catch(console.error);
    } else {
      import(/* webpackChunkName: "share-modal" */ '../../../services/content-type/share-modal').then(module => {
        module.default(data.url, data.headline, data.id);
      });
    }
  };

  const onCloseReactions = () => {
    setIsReactionsOpen(false);
  };

  const onCloseSharingWidget = () => {
    setIsSharingModalOpen(false);
    SharingEventLogger.closeSharingWindowByIcon();
  };

  const renderVariantsButtons = () => {
    switch (variantButtons) {
      case VARIANTS_BUTTONS.COLORED_SAME_SIZE:
        return (
          <>
            {data.isCommentsEnabled &&
            <button className={styles.coloredSameSizeItem} onClick={onCommentsClick} ref={commentsButtonRef}>
              <CommentsIcon className={clsx(styles.itemImage, styles.commentsImage)} />
              <span className={styles.itemCount}>{data.commentsCount}</span>
            </button>
            }
            <button
              className={clsx(styles.coloredSameSizeItem, {
                [styles.activeItem]: isReactionsOpen
              })}
              onClick={onReactionsClick}
              ref={reactionsButtonRef}
            >
              <ReactionIcon className={styles.itemImage} />
              <span className={styles.itemCount}>{data.reactionsCount}</span>
            </button>
            {shouldRenderShareButton && (
              <button
                className={clsx(styles.coloredSameSizeItem, {
                  [styles.activeItem]: isSharingModalOpen
                })}
                onClick={onShareBlockClick}
                ref={shareButtonRef}
              >
                <ShareIcon className={styles.itemImage} />
              </button>
            )}
          </>
        );
      case VARIANTS_BUTTONS.COLORED_DIFFERENT_SIZES:
        return (
          <>
            {data.isCommentsEnabled &&
            <button className={styles.coloredDifferentSizesItem} onClick={onCommentsClick} ref={commentsButtonRef}>
              <CommentsIcon className={clsx(styles.itemImage, styles.commentsImage)} />
              <span className={styles.itemCount}>
                <span className={styles.coloredDifferentSizesText}>{t('articleColoredDifferentSizesText')}:{' '}</span>
                {data.commentsCount}
              </span>
            </button>
            }
            <button
              className={clsx(styles.coloredDifferentSizesItem, {
                [styles.activeItem]: isReactionsOpen
              })}
              onClick={onReactionsClick}
              ref={reactionsButtonRef}
            >
              <ReactionIcon className={styles.itemImage} />
            </button>
            {shouldRenderShareButton && (
              <button
                className={clsx(styles.coloredDifferentSizesItem, {
                  [styles.activeItem]: isSharingModalOpen
                })}
                onClick={onShareBlockClick}
                ref={shareButtonRef}
              >
                <ShareIcon className={styles.itemImage} />
              </button>
            )}
          </>);
      default:
      case VARIANTS_BUTTONS.DEFAULT_DESIGN:
        return (
          <>
            {data.isCommentsEnabled &&
            <button className={styles.item} onClick={onCommentsClick} ref={commentsButtonRef}>
              <CommentsIcon className={clsx(styles.itemImage, styles.commentsImage)} />
              <span className={styles.itemCount}>{data.commentsCount}</span>
            </button>
            }
            <button
              className={clsx(styles.item, {
                [styles.activeItem]: isReactionsOpen
              })}
              onClick={onReactionsClick}
              ref={reactionsButtonRef}
            >
              <ReactionIcon className={styles.itemImage} />
              <span className={styles.itemCount}>{data.reactionsCount}</span>
            </button>
            {shouldRenderShareButton && (
              <button
                className={clsx(styles.item, {
                  [styles.activeItem]: isSharingModalOpen
                })}
                onClick={onShareBlockClick}
                ref={shareButtonRef}
              >
                <ShareIcon className={styles.itemImage} />
              </button>
            )}
          </>
        );
    }
  };

  return (
    <div
      className={clsx(getContainerStyles(variantButtons), {
        [styles.fluidLayout]: !shouldRenderShareButton
      })}
    >
      {renderVariantsButtons()}
      <BookmarkAddButton articleId={articleId} />
      {isReactionsOpen && (
        <div
          className={clsx(styles.reactionsWrapper, {
            [styles.smallReactionsWrapper]: !data.isCommentsEnabled
          })}
          ref={ref}
        >
          <ArticleReactionsWidget
            callback={onCloseReactions}
            articleId={articleId}
            variant={variant}
          />
        </div>
      )}
      {isSharingModalOpen && (
        <div
          className={clsx(styles.sharingModalContainer, {
            [styles.smallharingModalContainer]: !data.isCommentsEnabled
          })}
          ref={shareContainerRef}
        >
          <SharingWidget
            onClose={onCloseSharingWidget}
            onCopy={() => SharingEventLogger.shareBySocial(SHARING_COPY)}
            onShare={type => SharingEventLogger.shareBySocial(type)}
            url={data.url}
            title={data.headline}
            utmMedium={layoutSection}
          />
        </div>
      )}
    </div>
  );
};

export {ArticleBottomNav};
